import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

/**
 * Field Experiment Click Tracker
 *
 * This component tracks clicks from field experiment emails.
 * URLs: /field-track/:emailId (e.g., /field-track/e1, /field-track/e5)
 *
 * Logs the click to the backend and shows a simple thank you message.
 * No deception - just tracks which emails people click on.
 */

const FieldExperimentTracker: React.FC = () => {
  const { emailId } = useParams<{ emailId: string }>();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const logClick = async () => {
      try {
        // Get participant info from URL if provided (optional)
        const participantId = searchParams.get('p');
        const timestamp = new Date().toISOString();

        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

        // Log the click
        const response = await fetch(`${API_BASE_URL}/field-experiment/track-click`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            emailId,
            participantId,
            timestamp,
            userAgent: navigator.userAgent,
            referrer: document.referrer
          })
        });

        if (!response.ok) {
          throw new Error('Failed to log click');
        }

        setLoading(false);
      } catch (err) {
        console.error('Error logging click:', err);
        setError('An error occurred');
        setLoading(false);
      }
    };

    if (emailId) {
      logClick();
    }
  }, [emailId, searchParams]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '600px',
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        {error ? (
          <>
            <h1 style={{ color: '#d32f2f', marginBottom: '20px' }}>Error</h1>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: 1.6 }}>
              An error occurred processing your request. Please try again later.
            </p>
          </>
        ) : (
          <>
            <h1 style={{ color: '#1976d2', marginBottom: '20px' }}>Thank You</h1>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: 1.6 }}>
              Thank you for your response. You may now close this window.
            </p>
            <p style={{ fontSize: '14px', color: '#999', marginTop: '30px' }}>
              University of Colorado Colorado Springs
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default FieldExperimentTracker;
